import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, Loader2, Plus, Trash2 } from 'lucide-react'
import {
  createPack, deletePack, deletePackPrice, listPacks, majorToMinor,
  minorToMajor, updatePack, upsertPackPrice, type AdminPack,
} from '@/api/billingAdmin'
import { listPlans, type AdminPlan } from '@/api/billingAdmin'
import { COUNTRIES } from '@/data/countries'
import { AdminModal, Field, InfoBox, Td, Th, chipStyle, errText, tableStyle } from '../adminUi'

const CURRENCIES = ['USD', 'INR', 'EUR', 'GBP', 'AED', 'SGD']

export default function CreditPacksPanel() {
  const [packs, setPacks] = useState<AdminPack[]>([])
  const [plans, setPlans] = useState<AdminPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<AdminPack | null>(null)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    try {
      const [p, pl] = await Promise.all([listPacks(), listPlans()])
      setPacks(p.packs)
      setPlans(pl.plans)
    } catch (e) {
      toast.error(errText(e, 'Could not load credit packs'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const remove = async (pack: AdminPack) => {
    if (!window.confirm(`Delete the "${pack.name}" pack? Users will no longer be able to buy it.`)) return
    try {
      await deletePack(pack.id)
      await load()
    } catch (e) {
      toast.error(errText(e, 'Could not delete the pack'))
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><Loader2 className="spin" /></div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <InfoBox>
        Packs are how users buy credits when their balance runs out. Each pack has
        a price per currency (with optional per-country overrides), the credits it
        grants, and any bonus credits on top. Purchased credits never expire.
      </InfoBox>

      {!packs.length && (
        <div style={{
          display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.75rem 0.875rem',
          background: 'var(--color-bg-secondary)', border: '1px solid var(--color-warning, #f0a020)',
          borderRadius: 6, fontSize: '0.875rem',
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--color-warning, #f0a020)', flexShrink: 0 }} />
          No packs are configured, so users who run out of credits have no way to
          buy more. Add at least one.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>
          <Plus size={14} /> New pack
        </button>
      </div>

      <table style={tableStyle}>
        <thead>
          <tr>
            <Th>Code</Th><Th>Name</Th><Th right>Credits</Th><Th right>Bonus</Th>
            <Th>Prices</Th><Th>Visible to</Th><Th right>Active</Th><Th />
          </tr>
        </thead>
        <tbody>
          {packs.map((pack) => (
            <tr key={pack.id}>
              <Td><span style={chipStyle}>{pack.code}</span></Td>
              <Td>{pack.name}</Td>
              <Td right>{pack.credits.toLocaleString()}</Td>
              <Td right>{pack.bonus_credits ? `+${pack.bonus_credits.toLocaleString()}` : '—'}</Td>
              <Td>
                {pack.prices.length
                  ? pack.prices.map((p) => (
                      <span key={p.id} style={{ ...chipStyle, marginRight: 4 }}>
                        {p.currency}{p.country ? `/${p.country}` : ''} {minorToMajor(p.amount_minor)}
                      </span>
                    ))
                  : <span style={{ color: 'var(--color-warning, #f0a020)' }}>No price — unbuyable</span>}
              </Td>
              <Td>
                {pack.visible_plan_codes?.length
                  ? pack.visible_plan_codes.join(', ')
                  : <span style={{ color: 'var(--color-text-secondary)' }}>All plans</span>}
              </Td>
              <Td right>{pack.is_active ? 'Yes' : 'No'}</Td>
              <Td right>
                <button className="btn btn-secondary" style={{ padding: '0.15rem 0.5rem', marginRight: 4 }}
                        onClick={() => setEditing(pack)}>Edit</button>
                <button className="btn btn-secondary" style={{ padding: '0.15rem 0.35rem' }}
                        onClick={() => remove(pack)}><Trash2 size={12} /></button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>

      {(editing || creating) && (
        <PackEditor
          pack={editing}
          plans={plans}
          onClose={() => { setEditing(null); setCreating(false) }}
          onSaved={async () => { setEditing(null); setCreating(false); await load() }}
        />
      )}
    </div>
  )
}

function PackEditor({ pack, plans, onClose, onSaved }: {
  pack: AdminPack | null
  plans: AdminPlan[]
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const isNew = pack === null
  const [form, setForm] = useState<Partial<AdminPack>>(pack ?? {
    code: '', name: '', credits: 0, bonus_credits: 0,
    visible_plan_codes: null, is_active: true, sort_order: 50,
  })
  const [prices, setPrices] = useState(pack?.prices ?? [])
  const [saving, setSaving] = useState(false)
  const [currency, setCurrency] = useState('USD')
  const [country, setCountry] = useState('')
  const [amount, setAmount] = useState('')

  const toggleVisible = (code: string) => {
    setForm((f) => {
      const current = f.visible_plan_codes
      // null means "every plan" — the first tick has to start from that
      // meaning, not from an empty list, or ticking one plan would silently
      // hide the pack from all the others.
      if (current === null || current === undefined) {
        return { ...f, visible_plan_codes: plans.map((p) => p.code).filter((c) => c !== code) }
      }
      const next = current.includes(code)
        ? current.filter((c) => c !== code)
        : [...current, code]
      return { ...f, visible_plan_codes: next.length === plans.length ? null : next }
    })
  }

  const save = async () => {
    if (!form.code || !form.name) {
      toast.error('A code and a name are required')
      return
    }
    setSaving(true)
    try {
      if (isNew) await createPack(form)
      else await updatePack(pack.id, form)
      toast.success('Pack saved')
      await onSaved()
    } catch (e) {
      toast.error(errText(e, 'Could not save the pack'))
    } finally {
      setSaving(false)
    }
  }

  const addPrice = async () => {
    if (isNew) { toast.error('Save the pack first, then add its prices'); return }
    if (!amount) { toast.error('Enter an amount'); return }
    try {
      const res = await upsertPackPrice(pack.id, {
        currency, country: country || null, amount_minor: majorToMinor(amount),
      })
      setPrices(res.packs.find((p) => p.id === pack.id)?.prices ?? [])
      setAmount('')
    } catch (e) {
      toast.error(errText(e, 'Could not save the price'))
    }
  }

  const visible = form.visible_plan_codes

  return (
    <AdminModal title={isNew ? 'New credit pack' : `Edit ${pack?.name}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 2fr' }}>
          <Field label="Code" hint="Cannot change later.">
            <input value={form.code || ''} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                   disabled={!isNew} style={{ width: '100%' }} />
          </Field>
          <Field label="Name" hint="What the user sees on the pack card.">
            <input value={form.name || ''} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                   style={{ width: '100%' }} />
          </Field>
        </div>

        <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
          <Field label="Credits">
            <input type="number" min={0} value={form.credits ?? 0}
                   onChange={(e) => setForm((f) => ({ ...f, credits: parseFloat(e.target.value) || 0 }))}
                   style={{ width: '100%' }} />
          </Field>
          <Field label="Bonus credits" hint="Free credits on top — shown as a bonus on the card.">
            <input type="number" min={0} value={form.bonus_credits ?? 0}
                   onChange={(e) => setForm((f) => ({ ...f, bonus_credits: parseFloat(e.target.value) || 0 }))}
                   style={{ width: '100%' }} />
          </Field>
        </div>

        <div>
          <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '0.375rem' }}>
            Visible to plans
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {plans.map((p) => (
              <label key={p.code} style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', fontSize: '0.875rem' }}>
                <input
                  type="checkbox"
                  checked={visible === null || visible === undefined || visible.includes(p.code)}
                  onChange={() => toggleVisible(p.code)}
                />
                {p.display_name}
              </label>
            ))}
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            All ticked = offered on every plan, including plans added later.
          </div>
        </div>

        {!isNew && (
          <div>
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.375rem' }}>Prices</div>
            <table style={{ ...tableStyle, marginTop: 0 }}>
              <thead><tr><Th>Currency</Th><Th>Country</Th><Th right>Amount</Th><Th /></tr></thead>
              <tbody>
                {prices.map((p) => (
                  <tr key={p.id}>
                    <Td>{p.currency}</Td>
                    <Td>{p.country || <span style={{ color: 'var(--color-text-secondary)' }}>default</span>}</Td>
                    <Td right>{minorToMajor(p.amount_minor)}</Td>
                    <Td right>
                      <button className="btn btn-secondary" style={{ padding: '0.1rem 0.35rem' }}
                              onClick={async () => {
                                await deletePackPrice(pack.id, p.id)
                                setPrices((rows) => rows.filter((r) => r.id !== p.id))
                              }}>
                        <Trash2 size={12} />
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '1fr 1.6fr 1fr auto', marginTop: '0.5rem' }}>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={country} onChange={(e) => setCountry(e.target.value)}>
                <option value="">Default for currency</option>
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
              <input type="number" min={0} step="0.01" placeholder="Amount" value={amount}
                     onChange={(e) => setAmount(e.target.value)} />
              <button className="btn btn-secondary" onClick={addPrice}><Plus size={14} /></button>
            </div>
          </div>
        )}

        <label style={{ display: 'flex', gap: '0.375rem', alignItems: 'center', fontSize: '0.875rem' }}>
          <input type="checkbox" checked={!!form.is_active}
                 onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
          Active
        </label>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <Loader2 size={14} className="spin" /> : 'Save pack'}
          </button>
        </div>
      </div>
    </AdminModal>
  )
}
